if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };

        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.isVerticalThumbs =
          this.mql.matches && !!this.closest('.product--thumbnail, .product--thumbnail_right');
        this.initThumbnailNav();

        this.updateThumbArrows = this.updateThumbArrows.bind(this);
        this.mql.addEventListener?.('change', this.updateThumbArrows);
        window.addEventListener('resize', this.updateThumbArrows);

        this.elements.viewer.addEventListener(
          'slideChanged',
          debounce(this.onSlideChanged.bind(this), 500)
        );

        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          const btn = mediaToSwitch.querySelector('button');
          if (!btn) return;
          btn.addEventListener(
            'click',
            this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false)
          );
        });

        if (this.dataset.desktopLayout?.includes('thumbnail') && this.mql.matches) {
          this.removeListSemantic();
        }

        if (this.dataset.initialMedia) this.showInitialMedia();
      }

      // "Start the gallery with media no.": the media is already active in the
      // markup; scroll the slider and the thumbnails to it, never the page.
      showInitialMedia() {
        const id = this.dataset.initialMedia;
        const media = this.elements.viewer?.querySelector(`[data-media-id="${id}"]`);
        if (!media) return;
        const list = media.parentElement;
        // Instant: the slider is smooth-scrolling by CSS, and on load it should
        // simply open there.
        if (list.scrollWidth > list.clientWidth) list.scrollTo({ left: media.offsetLeft, behavior: 'instant' });
        const thumbnail = this.elements.thumbnails?.querySelector(`[data-target="${id}"]`);
        if (thumbnail) this.setActiveThumbnail(thumbnail);
      }

      initThumbnailNav() {
        if (!this.isVerticalThumbs) return;

        const scroller = this.querySelector('.thumbnail-slider .thumbnail-list.slider--tablet-up');
        if (!scroller) return;

        const prevCandidate = this.querySelector(
          '.thumbnail-slider .slider-button--prev, .thumbnail-slider button[name="previous"]'
        );
        const nextCandidate = this.querySelector(
          '.thumbnail-slider .slider-button--next, .thumbnail-slider button[name="next"]'
        );

        const normalizeBtn = (el) => (el ? (el.tagName === 'BUTTON' ? el : el.querySelector('button')) : null);

        const prevBtn = normalizeBtn(prevCandidate);
        const nextBtn = normalizeBtn(nextCandidate);
        if (!prevBtn || !nextBtn) return;

        this._thumbScroller = scroller;
        this._prevBtn = prevBtn;
        this._nextBtn = nextBtn;

        [prevBtn, nextBtn].forEach((btn) => {
          btn.removeAttribute('disabled');
          btn.removeAttribute('aria-disabled');
          btn.classList.remove('disabled');
          btn.style.pointerEvents = 'auto';
          btn.type = 'button';
        });

        const step = () => Math.max(80, Math.floor(scroller.clientHeight * 0.75));

        const handle = (dir) => (e) => {
          e.preventDefault();
          e.stopPropagation();
          if (e.stopImmediatePropagation) e.stopImmediatePropagation();
          scroller.scrollBy({ top: dir * step(), behavior: 'smooth' });
        };

        prevBtn.addEventListener('pointerdown', handle(-1), true);
        nextBtn.addEventListener('pointerdown', handle(1), true);
        prevBtn.addEventListener('click', handle(-1), true);
        nextBtn.addEventListener('click', handle(1), true);

        scroller.addEventListener(
          'scroll',
          () => {
            this.updateThumbArrows();
          },
          { passive: true }
        );

        this.updateThumbArrows();
      }

      updateThumbArrows() {
        if (!this.isVerticalThumbs) return;
        if (!this._thumbScroller || !this._prevBtn || !this._nextBtn) return;

        const s = this._thumbScroller;

        const hasOverflow = s.scrollHeight > s.clientHeight + 2;
        if (!hasOverflow) {
          this._prevBtn.classList.add('thumb-arrow-hidden');
          this._nextBtn.classList.add('thumb-arrow-hidden');
          return;
        }

        const atTop = s.scrollTop <= 2;
        const atBottom = s.scrollTop + s.clientHeight >= s.scrollHeight - 2;

        this._prevBtn.classList.toggle('thumb-arrow-hidden', atTop);
        this._nextBtn.classList.toggle('thumb-arrow-hidden', atBottom);
      }

      onSlideChanged(event) {
        const mediaId = event.detail?.currentElement?.dataset?.mediaId;
        if (!mediaId) return;
        const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`);
        if (!activeMedia) return;

        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');

        if (prepend) {
          activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            if (activeThumbnail) activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();

        window.setTimeout(() => {
          if (this.elements.thumbnails) {
            if (this.isVerticalThumbs) {
              activeMedia.parentElement.scrollTo({ top: activeMedia.offsetTop, behavior: 'smooth' });
            } else {
              activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
            }
          }

          if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
            activeMedia.scrollIntoView({ behavior: 'smooth' });
          }
        });

        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;

        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);

        if (activeThumbnail) {
          this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
        }
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));

        const btn = thumbnail.querySelector('button');
        if (btn) btn.setAttribute('aria-current', true);

        this.elements.thumbnails.querySelectorAll('.thumbnail').forEach((element) => {
          element.classList.remove('is-active');
        });

        const thumbEl = thumbnail.querySelector('.thumbnail');
        if (thumbEl) thumbEl.classList.add('is-active');

        if (!this.isVerticalThumbs && this.elements.thumbnails.isSlideVisible) {
          if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;
        }

        if (this.isVerticalThumbs) {
          const scroller =
            this.querySelector('.thumbnail-slider .thumbnail-list.slider--tablet-up') ||
            this.elements.thumbnails.querySelector('.thumbnail-list.slider--tablet-up') ||
            this.elements.thumbnails;

          if (!scroller) return;

          const scrollerRect = scroller.getBoundingClientRect();
          const itemRect = thumbnail.getBoundingClientRect();
          const padding = 10;

          const isVisible =
            itemRect.top >= scrollerRect.top + padding &&
            itemRect.bottom <= scrollerRect.bottom - padding;

          if (!isVisible) {
            const top = thumbnail.offsetTop - Math.floor(scroller.clientHeight * 0.2);
            scroller.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
          }

          this.updateThumbArrows();
          return;
        }

        if (this.elements.thumbnails.slider) {
          this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
        }
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;

        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML =
            window.accessibilityStrings.imageAvailable.replace('[index]', position);

          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };

        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
